import { maybe, Maybe } from '@passionware/monads';
import { QueryClient, useQuery } from '@tanstack/react-query';
import {
  EnterpriseApi,
  EnterpriseListFilter,
  EnterpriseTeam,
  EnterpriseTeamsQuery,
  EnterpriseWorkspace
} from '../../../api/enterprise/enterprise.api';
import { ensureIdleQuery } from '../../../platform/ensureIdleQuery';
import { WithErrorService } from '../../front/ErrorService/ErrorService';
import { WithServices } from '../../../platform/ts/services';
import { EnterpriseService } from './EnterpriseService';
import {
  seedEnterpriseTeamListCaches,
  seedEnterpriseWorkspaceListCaches,
  teamByWorkspaceSlugQueryKey,
  teamIdQueryKey,
  teamSlugQueryKey,
  workspaceIdQueryKey,
  workspaceSlugQueryKey
} from './enterpriseService.cache';

export function createEnterpriseService(
  config: WithServices<[WithErrorService]> & {
    api: EnterpriseApi;
    queryClient: QueryClient;
  }
): EnterpriseService {
  const {
    api,
    queryClient,
    services: { errorService }
  } = config;
  const interceptor = errorService.withCategory('EnterpriseService');

  async function fetchWorkspaces(
    organizationId: string,
    filter: EnterpriseListFilter = 'active'
  ) {
    const workspaces = await interceptor.wrap(() =>
      api.getWorkspaces(organizationId, filter)
    )();
    seedEnterpriseWorkspaceListCaches(queryClient, workspaces, filter);
    return workspaces;
  }

  async function fetchTeams(
    query: EnterpriseTeamsQuery,
    filter: EnterpriseListFilter = 'active'
  ) {
    const teams = await interceptor.wrap(() => api.getTeams(query, filter))();
    seedEnterpriseTeamListCaches(
      queryClient,
      teams,
      query.organizationId,
      filter
    );
    return teams;
  }

  async function ensureWorkspaceBySlug(
    organizationId: string,
    slug: string,
    filter: EnterpriseListFilter = 'active'
  ): Promise<Maybe<EnterpriseWorkspace>> {
    const cached = queryClient.getQueryData<EnterpriseWorkspace>(
      workspaceSlugQueryKey(organizationId, slug, filter)
    );
    if (cached) {
      return maybe.of(cached);
    }

    const workspaces = await fetchWorkspaces(organizationId, filter);
    const match = workspaces.find(w => w.slug === slug);
    return maybe.fromTruthy(match);
  }

  async function ensureWorkspace(
    organizationId: string,
    id: number,
    filter: EnterpriseListFilter = 'active'
  ): Promise<Maybe<EnterpriseWorkspace>> {
    const cached = queryClient.getQueryData<EnterpriseWorkspace>(
      workspaceIdQueryKey(organizationId, id, filter)
    );
    if (cached) {
      return maybe.of(cached);
    }

    const workspaces = await fetchWorkspaces(organizationId, filter);
    const match = workspaces.find(w => w.id === id);
    return maybe.fromTruthy(match);
  }

  async function ensureTeam(
    organizationId: string,
    workspaceId: number,
    teamId: number,
    filter: EnterpriseListFilter = 'active'
  ): Promise<Maybe<EnterpriseTeam>> {
    const cached = queryClient.getQueryData<EnterpriseTeam>(
      teamIdQueryKey(organizationId, teamId, filter)
    );
    if (cached) {
      return maybe.of(cached);
    }

    const teams = await queryClient.fetchQuery({
      queryKey: ['enterprise', 'teams', organizationId, workspaceId, filter],
      queryFn: () => fetchTeams({ organizationId, workspaceId }, filter)
    });
    const match = teams.find(team => team.id === teamId);
    return maybe.fromTruthy(match);
  }

  async function ensureTeamBySlug(
    organizationId: string,
    workspaceSlug: string,
    teamSlug: string,
    filter: EnterpriseListFilter = 'active'
  ): Promise<Maybe<EnterpriseTeam>> {
    const workspace = await ensureWorkspaceBySlug(
      organizationId,
      workspaceSlug,
      filter
    );
    if (maybe.isAbsent(workspace)) {
      return maybe.ofAbsent();
    }

    const cached = queryClient.getQueryData<EnterpriseTeam>(
      teamSlugQueryKey(organizationId, workspace.id, teamSlug, filter)
    );
    if (cached) {
      return maybe.of(cached);
    }

    const teams = await fetchTeams(
      { organizationId, workspaceId: workspace.id },
      filter
    );
    const match = teams.find(t => t.slug === teamSlug);
    return maybe.fromTruthy(match);
  }

  return {
    useWorkspaces: (organizationId, filter = 'active') =>
      ensureIdleQuery(
        organizationId,
        useQuery(
          {
            enabled: maybe.isPresent(organizationId),
            queryKey: ['enterprise', 'workspaces', organizationId, filter],
            queryFn: () => fetchWorkspaces(organizationId!, filter)
          },
          queryClient
        )
      ),
    ensureWorkspaces: (organizationId, filter = 'active') =>
      queryClient.fetchQuery({
        queryKey: ['enterprise', 'workspaces', organizationId, filter],
        queryFn: () => fetchWorkspaces(organizationId, filter)
      }),
    useTeams: (query, filter = 'active') =>
      ensureIdleQuery(
        query,
        useQuery(
          {
            enabled: maybe.isPresent(query),
            queryKey: [
              'enterprise',
              'teams',
              query?.organizationId,
              query?.workspaceId,
              filter
            ],
            queryFn: () => fetchTeams(query!, filter)
          },
          queryClient
        )
      ),
    ensureTeams: (query, filter = 'active') => {
      if (!maybe.isPresent(query)) {
        return Promise.resolve([]);
      }
      return queryClient.fetchQuery({
        queryKey: [
          'enterprise',
          'teams',
          query.organizationId,
          query.workspaceId,
          filter
        ],
        queryFn: () => fetchTeams(query, filter)
      });
    },
    useWorkspaceBySlug: (organizationId, slug, filter = 'active') =>
      ensureIdleQuery(
        maybe.combine({ organizationId, slug }),
        useQuery(
          {
            enabled: maybe.isPresent(organizationId) && maybe.isPresent(slug),
            queryKey: workspaceSlugQueryKey(organizationId!, slug!, filter),
            queryFn: () => ensureWorkspaceBySlug(organizationId!, slug!, filter)
          },
          queryClient
        )
      ),
    ensureWorkspaceBySlug,
    useWorkspace: (organizationId, id) =>
      ensureIdleQuery(
        id != null ? maybe.of(id) : maybe.ofAbsent(),
        useQuery(
          {
            enabled: id != null,
            queryKey: workspaceIdQueryKey(organizationId, id!, 'active'),
            queryFn: () => ensureWorkspace(organizationId, id!)
          },
          queryClient
        )
      ),
    ensureWorkspace,
    ensureTeam,
    useTeamBySlug: (
      organizationId,
      workspaceSlug,
      teamSlug,
      filter = 'active'
    ) =>
      ensureIdleQuery(
        maybe.combine({ organizationId, workspaceSlug, teamSlug }),
        useQuery(
          {
            enabled:
              maybe.isPresent(organizationId) &&
              maybe.isPresent(workspaceSlug) &&
              maybe.isPresent(teamSlug),
            queryKey: teamByWorkspaceSlugQueryKey(
              organizationId!,
              workspaceSlug!,
              teamSlug!,
              filter
            ),
            queryFn: () =>
              ensureTeamBySlug(
                organizationId!,
                workspaceSlug!,
                teamSlug!,
                filter
              )
          },
          queryClient
        )
      ),
    ensureTeamBySlug
  };
}
